import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import { exportToExcel, exportToPDF } from '../utils/exportUtils';
import SucursalChips from './SucursalChips';
import { isParking, isBotica } from '../hooks/useSucursal';
import ParkingDashboard from './ParkingDashboard';
import DashboardBotica from './DashboardBotica';
import './styles/Dashboard.css';

const Dashboard = () => {
    // 🚗 Si el negocio es Parking / Estacionamiento, renderizar su Dashboard especializado
    if (isParking()) {
        return <ParkingDashboard />;
    }

    // 💊 Si el negocio es Botica / Farmacia, renderizar su Dashboard especializado
    if (isBotica()) {
        return <DashboardBotica />;
    }

    const [stats, setStats]           = useState(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState(null);
    const [period, setPeriod]         = useState('today');
    const [startDate, setStartDate]   = useState('');
    const [endDate, setEndDate]       = useState('');
    const [showCustomDates, setShowCustomDates] = useState(false);
    // Filtro de sucursal (null = todas las sucursales)
    const [sucursalFiltro, setSucursalFiltro] = useState(null);


    useEffect(() => {
        loadStats();
    }, [period, startDate, endDate, sucursalFiltro]);

    const loadStats = useCallback(async () => {
        if (period === 'custom' && (!startDate || !endDate)) return;
        try {
            setLoading(true);
            const params = { period };
            if (period === 'custom') { params.startDate = startDate; params.endDate = endDate; }
            if (sucursalFiltro)      { params.sucursal_id = sucursalFiltro; }

            const response = await dashboardAPI.getStats(params);
            setStats(response.data.data);
            setError(null);
        } catch (err) {
            console.error('Error al cargar estadísticas:', err);
            setError(err.response?.data?.message || 'Error al cargar las estadísticas');
        } finally {
            setLoading(false);
        }
    }, [period, startDate, endDate, sucursalFiltro]);

    const handlePeriodChange = (newPeriod) => {
        setPeriod(newPeriod);
        setShowCustomDates(newPeriod === 'custom');
        if (newPeriod !== 'custom') {
            setStartDate('');
            setEndDate('');
        }
    };

    const getPeriodLabel = () => {
        const labels = {
            'today': 'Hoy',
            'week': 'Esta Semana',
            'month': 'Este Mes',
            'year': 'Este Año',
            'custom': `${startDate} al ${endDate}`
        };
        return labels[period] || 'Este Mes';
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN'
        }).format(value);
    };

    const ChangeIndicator = ({ value }) => {
        if (!value || value === 0) return null;
        
        if (value > 999) {
            return <span className="change-indicator positive">🆕</span>;
        }
        
        if (value < -99) {
            return <span className="change-indicator negative">↓</span>;
        }

        const isPositive = value > 0;
        return (
            <span className={`change-indicator ${isPositive ? 'positive' : 'negative'}`}>
                {isPositive ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
            </span>
        );
    };

    const StatCard = ({ icon, title, value, subtitle, change, warning }) => (
        <div className={`stat-card ${warning ? 'stat-warning' : ''}`}>
            <div className="stat-icon">{icon}</div>
            <div className="stat-content">
                <h3>{title}</h3>
                <div className="stat-number">
                    {value}
                    {change !== undefined && <ChangeIndicator value={change} />}
                </div>
                {subtitle && <div className="stat-label">{subtitle}</div>}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner"></div>
                <p>Cargando estadísticas...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <div className="error-icon">⚠️</div>
                <p>{error}</p>
                <button onClick={loadStats} className="btn btn-primary">
                    🔄 Reintentar
                </button>
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div className="dashboard-container" style={{ width: '100%', maxWidth: '100%' }}>

            {/* Selector de Sucursal — solo visible para admins */}
            <SucursalChips
                sucursalActiva={sucursalFiltro}
                onChange={setSucursalFiltro}
            />

            {/* Header */}
            <div className="dashboard-header">
                <div className="header-title">
                    <h1>📊 Dashboard</h1>
                    <p className="header-subtitle">
                        {sucursalFiltro
                            ? `Estadísticas filtradas por sucursal #${sucursalFiltro}`
                            : 'Visión general de tu negocio en tiempo real'}
                    </p>
                </div>

                <div className="dashboard-actions">
                    {/* Filtros de período */}
                    <div className="period-filters">
                        {['today', 'week', 'month', 'year', 'custom'].map((p) => (
                            <button
                                key={p}
                                className={`filter-btn ${period === p ? 'active' : ''}`}
                                onClick={() => handlePeriodChange(p)}
                            >
                                {p === 'custom' ? '📅 Personalizado' :
                                    p === 'today' ? 'Hoy' :
                                        p === 'week' ? 'Semana' :
                                            p === 'month' ? 'Mes' : 'Año'}
                            </button>
                        ))}
                    </div>

                    {/* Botones de acción */}
                    <div className="action-buttons">
                        <button
                            onClick={() => exportToExcel(stats, period).catch(err => console.error('Error al exportar Excel:', err))}
                            className="btn btn-success"
                            title="Exportar a Excel"
                        >
                            📊 Excel
                        </button>
                        <button
                            onClick={() => exportToPDF(stats, period)}
                            className="btn btn-danger"
                            title="Exportar a PDF"
                        >
                            📄 PDF
                        </button>
                        <button onClick={loadStats} className="btn btn-secondary">
                            🔄 Actualizar
                        </button>
                    </div>
                </div>
            </div>

            {/* Filtros de fecha personalizada */}
            {showCustomDates && (
                <div className="custom-date-filters" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div className="date-input-group">
                        <label>Desde:</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate || new Date().toISOString().split('T')[0]}
                            className="date-input"
                        />
                    </div>
                    <div className="date-input-group">
                        <label>Hasta:</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            className="date-input"
                        />
                    </div>
                    <button
                        onClick={loadStats}
                        className="btn btn-primary"
                        disabled={!startDate || !endDate}
                    >
                        Aplicar
                    </button>
                    {(!startDate || !endDate) && (
                        <span style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic', marginTop: '25px' }}>👈 Selecciona el rango de fechas</span>
                    )}
                </div>
            )}

            {/* Indicador de período activo */}
            <div className="period-indicator">
                <span>📆 Mostrando datos de: <strong>{getPeriodLabel()}</strong></span>
            </div>

            {/* Tarjetas de estadísticas principales */}
            <div className="stats-grid">
                <StatCard
                    icon="💰"
                    title="Ventas Hoy"
                    value={stats.today.sales}
                    subtitle={
                        <>
                            {formatCurrency(stats.today.revenue)}
                            <br />
                            {stats.today.pendingAmount > 0 && (
                                <>
                                    <small style={{ color: '#ea580c', fontWeight: 'bold' }}>
                                        ⏳ Por Cobrar (Fiados): {formatCurrency(stats.today.pendingAmount)}
                                    </small>
                                    <br />
                                </>
                            )}
                            <small style={{ color: '#e53935' }}>
                                📉 Gastos: {formatCurrency(stats.today.expenses)}
                            </small>
                            <br />
                            <small style={{ color: stats.today.profit >= 0 ? '#27ae60' : '#e53935', fontWeight: 'bold' }}>
                                {stats.today.profit >= 0 ? '✅' : '⚠️'} Ganancia Neta: {formatCurrency(stats.today.profit)}
                            </small>
                        </>
                    }
                />


                <StatCard
                    icon="📈"
                    title="TOTAL VENDIDO"
                    value={stats.period.sales}
                    subtitle={
                        <>
                            {formatCurrency(stats.period.revenue)}
                            <br />
                            {stats.period.pendingAmount > 0 && (
                                <>
                                    <small style={{ color: '#ea580c', fontWeight: 'bold' }}>
                                        ⏳ Por Cobrar (Fiados): {formatCurrency(stats.period.pendingAmount)}
                                    </small>
                                    <br />
                                </>
                            )}
                            <small style={{ color: '#e53935' }}>
                                📉 Gastos: {formatCurrency(stats.period.expenses)}
                            </small>
                            <br />
                            <small style={{ color: stats.period.profit >= 0 ? '#27ae60' : '#e53935', fontWeight: 'bold' }}>
                                {stats.period.profit >= 0 ? '✅' : '⚠️'} Ganancia Neta: {formatCurrency(stats.period.profit)}
                            </small>
                        </>
                    }
                    change={stats.period.profitChange}
                />

                <StatCard
                    icon="👥"
                    title="Clientes Totales"
                    value={stats.customers.total}
                    subtitle="Clientes registrados"
                />

                <StatCard
                    icon="📦"
                    title="Inventario"
                    value={
                        <>
                            {stats.inventory.lowStock > 0 && (
                                <div className="inventory-alert low-stock">
                                    ⚠️ Stock Bajo: {stats.inventory.lowStock}
                                </div>
                            )}
                            {stats.inventory.outOfStock > 0 && (
                                <div className="inventory-alert out-of-stock">
                                    🚫 Sin Stock: {stats.inventory.outOfStock}
                                </div>
                            )}
                            {stats.inventory.lowStock === 0 && stats.inventory.outOfStock === 0 && (
                                <div className="inventory-ok">✅ Todo OK</div>
                            )}
                        </>
                    }
                    warning={stats.inventory.outOfStock > 0}
                />
            </div>

            {/* Gráfico de Pastel - Ventas por Categoría */}
            {stats.salesByCategory && stats.salesByCategory.length > 0 && (
                <div className="pie-chart-section">
                    <div className="chart-card pie-chart-card">
                        <h3>🎯 Ventas por Categoría</h3>
                        <div className="pie-chart-container">
                            <svg viewBox="0 0 200 200" className="pie-chart">
                                {(() => {
                                    const total = stats.salesByCategory.reduce((sum, cat) =>
                                        sum + parseFloat(cat.total_sales), 0
                                    );
                                    let currentAngle = -90;
                                    const colors = [
                                        '#3498db', '#e74c3c', '#f39c12', '#27ae60', '#9b59b6',
                                        '#1abc9c', '#e67e22', '#34495e', '#16a085', '#2980b9'
                                    ];

                                    return stats.salesByCategory.map((category, index) => {
                                        const percentage = (parseFloat(category.total_sales) / total) * 100;

                                        // ✨ AQUÍ ESTÁ EL TRUCO APLICADO ✨
                                        let angle = (percentage / 100) * 360;
                                        if (angle === 360) angle = 359.99;

                                        const startAngle = currentAngle;
                                        const endAngle = currentAngle + angle;

                                        const startX = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
                                        const startY = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
                                        const endX = 100 + 80 * Math.cos((endAngle * Math.PI) / 180);
                                        const endY = 100 + 80 * Math.sin((endAngle * Math.PI) / 180);

                                        const largeArcFlag = angle > 180 ? 1 : 0;
                                        const pathData = [
                                            `M 100 100`,
                                            `L ${startX} ${startY}`,
                                            `A 80 80 0 ${largeArcFlag} 1 ${endX} ${endY}`,
                                            'Z'
                                        ].join(' ');

                                        currentAngle = endAngle;

                                        return (
                                            <path
                                                key={index}
                                                d={pathData}
                                                fill={colors[index % colors.length]}
                                                className="pie-slice"
                                            >
                                                <title>
                                                    {`${category.category}: ${formatCurrency(parseFloat(category.total_sales))} (${percentage.toFixed(1)}%)`}
                                                </title>
                                            </path>
                                        );
                                    });
                                })()}
                            </svg>

                            <div className="pie-chart-legend">
                                {stats.salesByCategory.map((category, index) => {
                                    const total = stats.salesByCategory.reduce((sum, cat) =>
                                        sum + parseFloat(cat.total_sales), 0
                                    );
                                    const percentage = (parseFloat(category.total_sales) / total) * 100;
                                    const colors = [
                                        '#3498db', '#e74c3c', '#f39c12', '#27ae60', '#9b59b6',
                                        '#1abc9c', '#e67e22', '#34495e', '#16a085', '#2980b9'
                                    ];

                                    return (
                                        <div key={index} className="legend-item">
                                            <span
                                                className="legend-color"
                                                style={{ backgroundColor: colors[index % colors.length] }}
                                            />
                                            <span className="legend-label">{category.category}</span>
                                            <span className="legend-value">
                                                {formatCurrency(parseFloat(category.total_sales))}
                                                <small>({percentage.toFixed(1)}%)</small>
                                                <small style={{ display: 'block', color: '#94a3b8', marginTop: '2px' }}>
                                                    🛒 N° de ventas: {parseInt(category.sales_count)}
                                                </small>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Gráficos y tablas */}
            <div className="dashboard-charts">
                {/* Ventas del período */}
                <div className="chart-card">
                    <h3>📊 Ventas - {getPeriodLabel()}</h3>
                    <div className="chart-container">
                        <div className="bar-chart">
                            {stats.salesChart.length > 0 ? (
                                stats.salesChart.map((day, index) => {
                                    const maxAmount = Math.max(...stats.salesChart.map(d =>
                                        parseFloat(d.total_amount)
                                    ));
                                    const height = maxAmount > 0
                                        ? (parseFloat(day.total_amount) / maxAmount) * 100
                                        : 0;

                                    return (
                                        <div key={index} className="bar-wrapper">
                                            <div className="bar-info">
                                                <span className="bar-amount">
                                                    {formatCurrency(parseFloat(day.total_amount))}
                                                </span>
                                                <span className="bar-count">({day.sales_count})</span>
                                            </div>
                                            <div
                                                className="bar"
                                                style={{ height: `${height || 5}%` }}
                                                title={`${day.date}: ${formatCurrency(parseFloat(day.total_amount))}`}
                                            ></div>
                                            <div className="bar-label">
                                                {new Date(day.date + 'T00:00:00').toLocaleDateString('es-PE', {
                                                    day: '2-digit',
                                                    month: 'short'
                                                })}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="no-data">
                                    <span className="no-data-icon">📭</span>
                                    <p>No hay ventas en este período</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Productos más vendidos */}
                <div className="chart-card">
                    <h3>🏆 Top 5 Productos Más Vendidos</h3>
                    <div className="table-container">
                        {stats.topProducts.length > 0 ? (
                            <table className="top-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Producto</th>
                                        <th>Veces Vendido</th>
                                        <th>Total Ventas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.topProducts.map((product, index) => (
                                        <tr key={index} className={index === 0 ? 'top-rank' : ''}>
                                            <td className="rank">
                                                {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                                            </td>
                                            <td className="product-name">{product.product_name}</td>
                                            <td className="quantity">{parseInt(product.times_sold)}</td>
                                            <td className="amount">
                                                {formatCurrency(parseFloat(product.total_sales))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="no-data">
                                <span className="no-data-icon">📦</span>
                                <p>No hay productos vendidos en este período</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Clientes frecuentes */}
                <div className="chart-card">
                    <h3>⭐ Top 5 Clientes Frecuentes</h3>
                    <div className="table-container">
                        {stats.topCustomers.length > 0 ? (
                            <table className="top-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Cliente</th>
                                        <th>Compras</th>
                                        <th>Total Gastado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.topCustomers.map((customer, index) => (
                                        <tr key={index} className={index === 0 ? 'top-rank' : ''}>
                                            <td className="rank">
                                                {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                                            </td>
                                            <td className="customer-name">{customer.name}</td>
                                            <td className="purchases">{customer.purchase_count}</td>
                                            <td className="amount">
                                                {formatCurrency(parseFloat(customer.total_spent))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="no-data">
                                <span className="no-data-icon">👥</span>
                                <p>No hay clientes con compras en este período</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;